import { type FC, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { IconButton, Menu } from 'shared/ui'
import { layoutStore } from 'shared/layout'
import { TooltipShortcut } from 'shared/ui/Tooltip/ui/TooltipShortcut'
import { EnumIconButtonShape } from 'shared/ui/IconButton'
import { TrialProgress } from 'entities/Subscription/ui/TrialProgress'
import { FeatureRestrictedModalStore } from 'entities/UsersPermissions'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { useEventEmitter } from 'entities/EventEmitter'
import { ConversationHeaderListDropdown } from 'pages/conversations/ui/ConversationHeaderListDropdown'
import {
  useConversationUnread,
  useEventConversationReassigned,
  useEventMessageCreatedDraft,
  useEventMessageDeletedDraft,
  useConversationMentionUnread,
} from 'pages/conversations/ui/ConversationMenu/events'
import {
  ConversationMenuContext,
  useConversationMenuContext,
} from 'pages/conversations/ui/ConversationMenu/context'
import { ConversationMenuInboxes } from 'pages/conversations/ui/ConversationMenu/ui/ConversationMenuInboxes/ConversationMenuInboxes'
import type { ConversationMenuStore } from 'pages/conversations/ui/ConversationMenu'
import { useEventSmartViewsConversationUnread } from 'pages/conversations/ui/ConversationMenu/events/EventSmartViewsConversationUnread'
import { useEventSmartViewsConversationReassigned } from 'pages/conversations/ui/ConversationMenu/events/EventSmartViewsConversationReassigned'
import styles from './styles.module.scss'

const ConversationMenuContent = observer(() => {
  const { conversationStore, usersStore, userPermissionsStore } = useEntitiesContext()
  const store = useConversationMenuContext()
  const { isMediumView, isLargeView, isMobileView } = layoutStore
  const isShouldRequestAccess = !!usersStore.user?.isShouldRequestAccess
  const pageLayoutStore = store.pageLayoutStore
  const quickFiltersMenuStore = store.quickFiltersMenuStore

  useConversationUnread(store)
  useConversationMentionUnread(store)
  useEventMessageCreatedDraft(store)
  useEventMessageDeletedDraft(store)
  useEventConversationReassigned(store)

  useEventSmartViewsConversationUnread()
  useEventSmartViewsConversationReassigned()

  useEventEmitter('draftMessageCreated', (payload) => {
    if (payload) {
      quickFiltersMenuStore.handleQuickFilterDraftCount({
        conversationId: payload.id,
        isIncrease: true,
      })

      conversationStore.increaseItemsTotalDraftCount(payload.id)
    }
  })

  useEventEmitter('draftMessageDeleted', (payload) => {
    if (payload) {
      quickFiltersMenuStore.handleQuickFilterDraftCount({
        conversationId: payload.conversation_id,
        isIncrease: false,
      })
    }
  })

  useEffect(() => {
    store.init()

    return () => {
      store.dispose()
    }
  }, [])

  const getHeaderButtons = () => {
    if (isLargeView) {
      return [
        {
          iconButtonComponent: (
            <IconButton
              onClick={() => {
                store.config?.onShowChats?.()
                store.config?.onToggleSearch?.()
              }}
              icon={'search'}
              disabled={isShouldRequestAccess}
              fontSize={20}
              shape={EnumIconButtonShape.Rounded}
              color={isShouldRequestAccess ? 'transparent' : 'secondary'}
              variant={'icon'}
              tooltipProps={
                !isShouldRequestAccess
                  ? {
                      placement: 'bottom',
                      label: <TooltipShortcut label={'Search'} keys={['ctrl', 'K']} />,
                      disableInteractive: true,
                    }
                  : undefined
              }
              ariaLabel='ConversationMenu_search'
            />
          ),
        },
        {
          iconButtonComponent: (
            <ConversationHeaderListDropdown
              disabled={isShouldRequestAccess}
              onChangeCall={() => {
                store.callModalStore.openModal()
              }}
              onChangeNew={() => {
                conversationStore.conversationOpenStore.handleOpenNewConversation()
              }}
              onChangeContactCreate={() => {
                userPermissionsStore.getItem('can_create_contacts')
                  ? store.contactCreateModalStore.openModal()
                  : new FeatureRestrictedModalStore('can_create_contacts')
              }}
              onChangeSmartViewCreate={() => {
                store.smartViewsMenuStore.onOpenModalCreate()
              }}
            />
          ),
        },
      ]
    }
    if (isMobileView) {
      return [
        {
          iconButtonComponent: (
            <IconButton
              onClick={() => {
                pageLayoutStore.handleChangeCollapse(true)
              }}
              icon={'close'}
              fontSize={20}
              color={'secondary'}
              variant={'icon'}
              tooltipProps={
                !isShouldRequestAccess
                  ? { placement: 'bottom', label: 'Search', disableInteractive: true }
                  : undefined
              }
              ariaLabel='ConversationMenu_search'
            />
          ),
        },
      ]
    }
  }

  if (pageLayoutStore.isCollapse) return null

  const isMobileViewStyle = isMediumView && !pageLayoutStore.isCollapse

  return (
    <div
      className={classNames(styles.wrap, {
        [styles.mobileView]: isMobileViewStyle,
      })}
    >
      <Menu
        title={'Inboxes'}
        headerButtons={getHeaderButtons()}
        menuContent={<ConversationMenuInboxes />}
        paddingRight={isMobileView ? 8 : 12}
        bottomNode={<TrialProgress />}
      />
    </div>
  )
})

type IConversationMenuProps = {
  store: ConversationMenuStore
}

export const ConversationMenu: FC<IConversationMenuProps> = observer(({ store }) => {
  return (
    <ConversationMenuContext.Provider value={store}>
      <ConversationMenuContent />
    </ConversationMenuContext.Provider>
  )
})
